import React, {useEffect} from 'react'
import {
  BrowserRouter as Router,
  Route,
  Routes,
  useLocation,
} from 'react-router-dom'
import {Provider} from 'react-redux'
import {store} from './redux/store'
import {
  getApiHookParams,
  identityAuth,
  IdentityAuthProvider,
  RoleBasedLanding,
  IdentityAuthGuard,
} from './auth'
import {createTheme, ThemeProvider} from '@mui/material/styles'
import paths from './routes/paths.js'
import Login from './views/Account/Login'
import SigninOidc from './views/Account/SignIn'
import Base from './components/layouts/Base'
import Dashboard from './views/Dashboard'
import NotFound from './views/NotFound.jsx'
import SurveyPanel from './components/layouts/SurveyPanel'
import QuestionPanel from './views/SurveyPanel/QuestionPanel'
import SurveyTemplates from './views/SurveyTemplates'
import InvitesPanel from './views/SurveyPanel/InvitesPanel'
import ReviewPanel from './views/SurveyPanel/ReviewPanel/index.js'
import SchedulePanel from './views/SurveyPanel/SchedulePanel'
import SurveyView from './views/SurveyView'
import SurveyParticipants from './views/SurveyPanel/SurveyParticipants'
import ResultsLayout from './components/layouts/ResultsLayout'
import Comments from './views/ResultsPanel/Comments'
import ParticipantReply from './views/ResultsPanel/Comments/ParticipantReply.jsx'
import Questions from './views/SuperAdmin/Questions'
import Redirect from './views/SuperAdmin/Redirect.jsx'
import EmojiLogin from './views/PupilDashboard/Login/Emoji/EmojiLogin.jsx'
import PupilSurveys from './views/PupilDashboard/PupilSurveys.jsx'
import QuestionTags from './views/SuperAdmin/Questions/QuestionTags.jsx'
import QrLogin from './views/PupilDashboard/Login/QrLogin.jsx'
import {trackPageView} from './utils/analytics'
import WidgitPointTest from './components/test/WidgitPointTest.jsx'
import CheckIn from './views/CheckInSurveys/CheckIn.jsx'
import SafeSpaceCheckinPanel from './components/layouts/SafeSpaceCheckinPanel/index.jsx'
import CheckInEdit from './views/CheckInSurveys/CheckInEdit.jsx'
import {useIdentityAuth} from './auth/IdentityAuthHook.js'
import ProtectedRoute from './routes/protectedRoutes.js'
import {Config} from './utils/Config.js'
import PublicRoute from './routes/publicRoute.js'
import KioskRootMessage from './views/Kiosk/KioskRootMessage'
import KioskApp from './views/Kiosk/KioskApp'
import KioskUrnScreen from './views/Kiosk/KioskUrnScreen'
import AdminSetup from './views/Kiosk/AdminSetup'
import ParentWelcome from './views/Kiosk/Parent/ParentWelcome'
import ParentSearch from './views/Kiosk/Parent/ParentSearch'
import ParentVerify from './views/Kiosk/Parent/ParentVerify'
import ParentSurveys from './views/Kiosk/Parent/ParentSurveys'
import ParentThankYou from './views/Kiosk/Parent/ParentThankYou'
import StudentWelcome from './views/Kiosk/Student/StudentWelcome'
import StudentSearch from './views/Kiosk/Student/StudentSearch'
import StudentVerify from './views/Kiosk/Student/StudentVerify'
import StudentSurveys from './views/Kiosk/Student/StudentSurveys'
import StudentThankYou from './views/Kiosk/Student/StudentThankYou'

// Route observer component to track page views
const RouteChangeTracker = () => {
  const location = useLocation()

  useEffect(() => {
    // Only track for authenticated users and non-login pages
    const skipTracking = ['/login', '/login-redirect', '/']

    if (!skipTracking.includes(location.pathname)) {
      // Track page view with current path
      const pageName =
        location.pathname.split('/').filter(Boolean).join('/') || 'dashboard'
      trackPageView(pageName, {
        path: location.pathname,
        search: location.search,
      })
    }
  }, [location])

  return null
}

function App() {
  const theme = createTheme({
    typography: {
      fontFamily: ['Inter, sans-serif'].join(','),
    },
  })

  useEffect(() => {
    // Remove session parameter from URL if present (security: JWT tokens shouldn't be in URL)
    const urlParams = new URLSearchParams(window.location.search)
    if (urlParams.has('session')) {
      urlParams.delete('session')
      const newSearch = urlParams.toString()
      const newUrl =
        window.location.pathname + (newSearch ? `?${newSearch}` : '')
      window.history.replaceState({}, '', newUrl)
    }

    const skipStorage = ['/login', '/login-redirect', '/']
    if (!skipStorage.includes(window.location.pathname)) {
      // Don't include session parameter in redirect URL
      const cleanUrlParams = new URLSearchParams(window.location.search)
      cleanUrlParams.delete('session')
      const cleanSearch = cleanUrlParams.toString()
      var url =
        window.location.pathname + (cleanSearch ? `?${cleanSearch}` : '')
      localStorage.setItem('RedirectUrl', url)
    }
  }, [])

  return (
    <ThemeProvider theme={theme}>
      <Provider store={store}>
        <IdentityAuthProvider>
          <Router>
            {/* Route tracker component to handle analytics */}
            <RouteChangeTracker />
            <Routes>
              {/* Kiosk routes (public, no authentication) - Must be before other routes */}
              <Route path="/kiosk" element={<KioskRootMessage />} />
              <Route path="/kiosk/:schoolSlug/*" element={<KioskApp />}>
                {/* Mode-selection (Parent/Student) renders at the bare /kiosk/:schoolSlug URL. */}
                <Route index element={<AdminSetup />} />
                <Route path="urn" element={<KioskUrnScreen />} />
                <Route path="parent" element={<ParentWelcome />} />
                <Route path="parent/search" element={<ParentSearch />} />
                <Route path="parent/verify" element={<ParentVerify />} />
                <Route path="parent/surveys" element={<ParentSurveys />} />
                <Route path="parent/thank-you" element={<ParentThankYou />} />
                <Route path="student" element={<StudentWelcome />} />
                <Route path="student/search" element={<StudentSearch />} />
                <Route path="student/verify" element={<StudentVerify />} />
                <Route path="student/surveys" element={<StudentSurveys />} />
                <Route path="student/thank-you" element={<StudentThankYou />} />
              </Route>

              <Route path={`${Config.IDENTITY_URL}/login`} />
              <Route element={<ProtectedRoute />}>
                <Route element={<Base />}>
                  <Route path={paths.dashboard} element={<Dashboard />} />
                  <Route element={<SurveyPanel />}>
                    <Route
                      path={paths.questionPanel}
                      element={<QuestionPanel />}
                    />
                    <Route
                      path={paths.surveyParticipants}
                      element={<SurveyParticipants />}
                    />
                    <Route
                      path={paths.invitesPanel}
                      element={<InvitesPanel />}
                    />
                    <Route
                      path={paths.schedulePanel}
                      element={<SchedulePanel />}
                    />
                    <Route path={paths.reviewPanel} element={<ReviewPanel />} />
                  </Route>
                  <Route path={paths.resultsDash} element={<ResultsLayout />}>
                    <Route path={paths.comments} element={<Comments />} />
                  </Route>
                </Route>
                <Route
                  path={paths.checkinroot}
                  element={<SafeSpaceCheckinPanel />}
                >
                  <Route path={paths.checkinSurveys} element={<CheckIn />} />
                  <Route
                    path={paths.checkinSurveysEdit}
                    element={<CheckInEdit />}
                  />
                </Route>
              </Route>
              <Route
                path={paths.surveyTemplatesPanel}
                element={<SurveyTemplates />}
              />
              {/* Public Routes - No Authentication Required - Example: ParticipantReply */}
              <Route element={<PublicRoute />}>
                <Route path={paths.surveyView} element={<SurveyView />} />
                <Route
                  path={paths.participantsReply}
                  element={<ParticipantReply />}
                />
              </Route>

              <Route path={paths.superAdminRedirect} element={<Redirect />} />
              <Route path={paths.pupilDashLogin} element={<EmojiLogin />} />
              <Route path={paths.qrCodeLogin} element={<QrLogin />} />
              <Route path={paths.pupilSurveys} element={<PupilSurveys />} />
              <Route path={paths.question} element={<Questions />} />
              <Route path={paths.questiontag} element={<QuestionTags />} />
              <Route path={paths.testWidgit} element={<WidgitPointTest />} />

              <Route path="*" element={<NotFound />} />
            </Routes>
          </Router>
        </IdentityAuthProvider>
      </Provider>
    </ThemeProvider>
  )
}

export default App
